import { Button, HXCircleIcon, Modal, Typography } from "@aph/ui-lib-humanx";
import { useRouter } from "next/navigation";
import React, { useState } from "react";
import { TOOL_DATE_FORMAT } from "@/components/ToolsPage/constants";
import { CalcInputComponent } from "@/components/ToolsPage/api/interface";
import InputTypeComponent from "@/components/ToolsPage/components/InputTypeComponent";
import { dateToString } from "@/helpers";
import routes from "@/helpers/router";
import { DeviceInfo } from "@/interface/deviceInfo";
import PeriodDate from "@/components/PageHome/components/OnboardingSection/Modals/PeriodDate";
import PeriodCycle from "@/components/PageHome/components/OnboardingSection/Modals/PeriodCycle";
import { DataSelectionsInterface } from "@/components/ToolsPage/redux/types";
import { TOOLS_SESSION_MAPPING, TOOLS_SLUG } from "@/helpers/constants";
import styles from "./CalculatorForm.module.scss";

interface CalculatorFormProps {
  data: CalcInputComponent;
  userAgent: DeviceInfo;
  toolSlug: string;
}

const initialCalcData = {
  dueDate: dateToString(new Date()),
  cycleLength: 28,
};

const CalculatorForm: React.FC<CalculatorFormProps> = ({ data, userAgent, toolSlug }) => {
  const { Title, CtaText } = data;

  const router = useRouter();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalInputType, setModalInputType] = useState<string>("");
  const [calcData, setCalcData] = useState<DataSelectionsInterface>(initialCalcData);

  const handleDatePickerClick = () => {
    setModalInputType("dateSelector");
    setIsModalOpen(true);
  };

  const handleCycleLengthClick = () => {
    setModalInputType("cycleSelector");
    setIsModalOpen(true);
  };

  const handleCalculatorSubmit = () => {
    sessionStorage.setItem(TOOLS_SESSION_MAPPING[toolSlug], JSON.stringify(calcData));
    router.push(routes.toolResult(toolSlug));
  };

  const handleModalInput: (data: DataSelectionsInterface) => void = (data) => {
    setCalcData((prevData) => ({ ...prevData, ...data }));
    setIsModalOpen(false);
  };

  return (
    <>
      <div className={styles.calculatorContainer}>
        <HXCircleIcon iconStyle="style2" className={styles.hxIcon} />
        {userAgent?.isDesktop && <HXCircleIcon iconStyle="style2" className={styles.hxIcon2} />}
        <Typography component="h2" className={styles.text}>
          <div dangerouslySetInnerHTML={{ __html: Title }} />
        </Typography>
        <div className={styles.inputsWrapper}>
          <div className={styles.datepick}>
            <InputTypeComponent
              className={styles.inputClass}
              label={"First Day Of Last Period"}
              value={calcData?.dueDate ? dateToString(calcData?.dueDate, TOOL_DATE_FORMAT) : ""}
              onClick={handleDatePickerClick}
              iconName={`icon-icon-calendar`}
            />
          </div>
          <div className={styles.datepick}>
            <InputTypeComponent
              className={styles.inputClass}
              label={"Average Cycle Length"}
              value={calcData?.cycleLength}
              onClick={handleCycleLengthClick}
              iconName={`icon-icon-left`}
              iconWrapperClass={styles.cycleLengthIcon}
            />
          </div>
        </div>

        {CtaText && (
          <Button size="lg" className={styles.cta} onClick={handleCalculatorSubmit}>
            {CtaText}
          </Button>
        )}
      </div>
      <Modal
        size="md"
        backdropClose
        mobileBottomAlign
        titleAlign="center"
        open={isModalOpen}
        handleOpen={setIsModalOpen}
        isHXModal
        modalClass={styles.modalClass}
        titleClass={styles.titleClass}
        modalboxClass={styles.modalboxClass}
        modalContentClass={styles.innerSection}
      >
        <>
          {modalInputType === "dateSelector" && (
            <PeriodDate
              initialDate={new Date(calcData?.dueDate)}
              ctaClick={(date) => {
                handleModalInput({ dueDate: dateToString(date) });
              }}
              ctaText="Submit"
              dateLimits={[275, 365]}
            />
          )}
          {modalInputType === "cycleSelector" && (
            <PeriodCycle
              initialValue={calcData?.cycleLength}
              ctaClick={(cycleLength) => handleModalInput({ cycleLength })}
              showEdgeCases={toolSlug === TOOLS_SLUG.OVULATION}
              ctaText="Submit"
            />
          )}
        </>
      </Modal>
    </>
  );
};

export default CalculatorForm;
